// src/components/admin/AdminExtensions.jsx
// Admin/instructor management page for student extension requests.
// Refined-editorial styling — cohesive with the rest of the portal.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import {
  FiCheck, FiX, FiChevronDown, FiChevronUp,
  FiClock, FiCheckCircle, FiXCircle, FiInbox,
  FiSearch, FiPlus, FiCalendar, FiBook,
  FiArrowLeft, FiArrowRight, FiUsers, FiFileText,
} from 'react-icons/fi';
import apiService from '../../services/api';
import logger from '../../utils/logger';

/* ============================ Layout ============================ */

const Page = styled.div`
  padding: 3rem 2.5rem 4.5rem;
  max-width: 1280px;
  margin: 0 auto;
  font-family: 'Inter', -apple-system, system-ui, sans-serif;
  background-color: var(--background-secondary);
  min-height: 100vh;
  @media (max-width: 768px) { padding: 2rem 1.5rem 3.5rem; }
  @media (max-width: 480px) { padding: 1.75rem 1rem 3rem; }
`;

/* Hero with accent underline */
const Hero = styled.header` margin-bottom: 2.5rem; `;

const HeroEyebrow = styled.div`
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.075em;
  text-transform: uppercase;
  color: var(--text-muted);
  margin-bottom: 0.75rem;
`;

const HeroTitle = styled.h1`
  font-family: 'Inter', -apple-system, system-ui, sans-serif;
  font-size: clamp(2rem, 3vw + 0.8rem, 2.8rem);
  font-weight: 700;
  letter-spacing: -0.032em;
  line-height: 1.15;
  margin: 0 0 0.75rem 0;
  color: var(--text-primary);
  display: inline-block;
  position: relative;
`;

HeroTitle.defaultProps = { as: 'h1' };

const HeroTitleWrap = styled.div`
  position: relative;
  display: inline-block;
  padding-bottom: 0.75rem;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    width: 3rem;
    height: 2.5px;
    border-radius: 2px;
    background: linear-gradient(90deg, var(--primary-color) 0%, rgba(184, 149, 106, 0) 100%);
  }
`;

const HeroSub = styled.p`
  font-size: 1rem;
  color: var(--text-muted);
  margin: 0;
  line-height: 1.6;
  max-width: 72ch;
  font-weight: 400;
`;

/* ============================ KPI Stat Tabs ============================ */
/* Behave like StudentQuizList: stat card doubles as the active status filter */
const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 2.5rem;
  @media (max-width: 1024px) { grid-template-columns: repeat(2, 1fr); gap: 0.875rem; }
  @media (max-width: 600px) { grid-template-columns: 1fr; gap: 0.75rem; }
`;

const StatCard = styled(motion.button)`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.625rem;
  padding: 1.25rem 1.5rem;
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08), 0 0 0 0.5px rgba(0, 0, 0, 0.03);
  font-family: 'Inter', -apple-system, system-ui, sans-serif;
  text-align: left;
  cursor: pointer;
  overflow: hidden;
  transition:
    all 0.2s cubic-bezier(0.22, 1, 0.36, 1);

  &:hover {
    border-color: var(--primary-color);
    box-shadow: 0 4px 12px rgba(184, 149, 106, 0.15), 0 0 0 1px var(--border-light);
    transform: translateY(-2px);
  }
`;

const StatStrip = styled.span`
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 3px;
  background: ${(p) => p.$color};
  opacity: 0.85;
`;

const StatLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.035em;
  text-transform: uppercase;
  color: var(--text-muted);
`;

const StatSwatch = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 7px;
  background: ${(p) => p.$tint};
  color: ${(p) => p.$color};
  flex-shrink: 0;
`;

const StatValue = styled.span`
  font-size: clamp(1.75rem, 2vw + 1rem, 2.25rem);
  font-weight: 700;
  letter-spacing: -0.025em;
  color: var(--text-primary);
  line-height: 1;
  font-variant-numeric: tabular-nums;
  margin-top: 0.25rem;
`;

const ContentShell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const SectionNav = styled.div`
  display: flex;
  gap: 10px;
  background: transparent;
  border-radius: 0;
  padding: 0;
  box-shadow: none;
  border: none;
  width: fit-content;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }

  @media (max-width: 600px) {
    width: 100%;
    gap: 8px;
    margin-bottom: 2rem;
  }
`;

const SectionNavItem = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 44px;
  min-width: 168px;
  padding: 0 1.75rem;
  border: 1.5px solid ${(p) => (p.$active ? 'var(--primary-color)' : 'var(--border-color)')};
  border-radius: 10px;
  color: ${(p) => (p.$active ? '#fff' : 'var(--text-secondary)')};
  background: ${(p) => (p.$active ? 'var(--primary-color)' : 'var(--background-primary)')};
  font-family: 'Inter', -apple-system, system-ui, sans-serif;
  font-size: 0.9375rem;
  font-weight: ${(p) => (p.$active ? 600 : 500)};
  text-align: center;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.22, 1, 0.36, 1);
  flex-shrink: 0;

  &:hover:not(:disabled) {
    ${(p) => p.$active ? `
      background: var(--primary-dark);
      border-color: var(--primary-dark);
      box-shadow: 0 4px 12px rgba(184, 149, 106, 0.25);
    ` : `
      border-color: var(--border-light);
      background: var(--background-secondary);
      color: var(--text-primary);
    `}
  }

  @media (max-width: 600px) {
    height: 42px;
    min-width: auto;
    flex: 1;
    padding: 0 1.25rem;
    font-size: 0.875rem;
  }
`;

const MainStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  min-width: 0;
  width: 100%;
`;

/* ============================ Filters Bar ============================ */

const FiltersBar = styled.div`
  display: grid;
  grid-template-columns: 2.5fr 1.2fr 1fr 1fr;
  align-items: center;
  gap: 12px;
  margin-bottom: 2rem;
  width: 100%;

  @media (max-width: 1024px) {
    grid-template-columns: 2fr 1fr 1fr 1fr;
    gap: 12px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 10px;
    margin-bottom: 1.75rem;
  }
`;

const SearchWrap = styled.div`
  position: relative;
  width: 100%;
`;

const SearchIcon = styled.span`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
  display: inline-flex;
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  height: 42px;
  padding: 0 14px 0 38px;
  border-radius: 9px;
  border: 1px solid var(--border-color);
  background: var(--background-primary);
  font-family: 'Inter', -apple-system, system-ui, sans-serif;
  font-size: 13px;
  color: var(--text-primary);
  box-sizing: border-box;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--border-light);
  }

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(184, 149, 106, 0.15), 0 0 0 1px var(--primary-color);
  }

  &::placeholder { color: var(--text-light); }
`;

const SelectWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  @media (max-width: 900px) {
    grid-column: auto;
  }
`;

const SelectIcon = styled.span`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
  display: inline-flex;
  pointer-events: none;
`;

const SelectChevron = styled.span`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
  display: inline-flex;
  pointer-events: none;
  font-weight: 700;
`;

const Select = styled.select`
  height: 42px;
  padding: 0 40px 0 38px;
  border-radius: 9px;
  border: 1px solid var(--border-color);
  background: var(--background-primary);
  font-family: 'Inter', -apple-system, system-ui, sans-serif;
  font-size: 13px;
  color: var(--text-primary);
  cursor: pointer;
  appearance: none;
  min-width: 140px;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--border-light);
  }

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(184, 149, 106, 0.15), 0 0 0 1px var(--primary-color);
  }
`;

const DateFilterWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DateLabelInline = styled.label`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--text-muted);
  white-space: nowrap;
  flex-shrink: 0;
`;

const DateInputWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  @media (max-width: 900px) {
    grid-column: auto;
  }
`;

const DateInputIcon = styled.span`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
  display: inline-flex;
  pointer-events: none;
`;

const DateInput = styled.input`
  height: 42px;
  padding: 0 14px 0 38px;
  border-radius: 9px;
  border: 1px solid var(--border-color);
  background: var(--background-primary);
  font-family: 'Inter', -apple-system, system-ui, sans-serif;
  font-size: 13px;
  color: var(--text-primary);
  box-sizing: border-box;
  min-width: 130px;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--border-light);
  }

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(184, 149, 106, 0.15), 0 0 0 1px var(--primary-color);
  }

  &::-webkit-calendar-picker-indicator {
    display: none !important;
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    display: none !important;
  }
`;

const GrantCard = styled.section`
  position: relative;
  background: var(--background-primary);
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03), 0 8px 24px -12px rgba(15, 23, 42, 0.08);

  /* Subtle top accent gradient */
  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--primary-color) 0%, #C6AB82 40%, #6b8e7f 100%);
    opacity: 0.85;
  }
`;

const GrantHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 2.25rem 2.25rem 1.75rem;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  margin-bottom: 0;
  background: linear-gradient(180deg, rgba(247, 245, 241, 0.5) 0%, transparent 100%);

  @media (max-width: 768px) {
    padding: 1.75rem 1.5rem 1.5rem;
  }
`;

const GrantTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  h2 {
    margin: 0;
    font-family: 'Inter', -apple-system, system-ui, sans-serif;
    font-size: clamp(1.25rem, 1.2vw + 0.9rem, 1.5rem);
    font-weight: 600;
    letter-spacing: -0.015em;
    color: var(--text-primary);
    line-height: 1.2;
  }
  p {
    margin: 6px 0 0;
    font-size: 0.9375rem;
    color: var(--text-muted);
    line-height: 1.5;
    max-width: 62ch;
    font-weight: 400;
    letter-spacing: 0;
  }
`;

const GrantGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 1rem;
  align-items: end;
  padding: 1.75rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.875rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
    padding: 1.5rem;
  }
`;

const GrantField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  grid-column: span ${(p) => p.$span || 4};
  min-width: 0;

  @media (max-width: 1024px) {
    grid-column: auto;
  }

  @media (max-width: 768px) {
    grid-column: 1 / -1;
  }
`;

const GrantLabel = styled.label`
  font-size: 0.6875rem;
  font-weight: 800;
  letter-spacing: 0.095em;
  text-transform: uppercase;
  color: #64748b;
`;

const GrantControlWrap = styled.div`
  position: relative;
`;

const GrantSelect = styled.select`
  width: 100%;
  height: 44px;
  padding: 0 42px 0 14px;
  border-radius: 14px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: rgba(255, 255, 255, 0.86);
  color: #0f172a;
  font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.875rem;
  font-weight: 550;
  box-sizing: border-box;
  cursor: pointer;
  appearance: none;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.16s ease, box-shadow 0.16s ease, background 0.16s ease;

  &:hover:not(:disabled) {
    border-color: rgba(107, 142, 127, 0.42);
    background: #fff;
  }

  &:focus {
    outline: none;
    border-color: #6b8e7f;
    box-shadow: 0 0 0 4px rgba(107, 142, 127, 0.14);
    background: #fff;
  }

  &:disabled {
    color: #94a3b8;
    background: rgba(241, 245, 249, 0.72);
    cursor: not-allowed;
  }
`;

const GrantChevron = styled.span`
  position: absolute;
  right: 13px;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  color: #6b8e7f;
  pointer-events: none;
`;

const GrantSearchIcon = styled.span`
  position: absolute;
  left: 13px;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  color: #8f7a58;
  pointer-events: none;
`;

const GrantInput = styled.input`
  width: 100%;
  height: 44px;
  padding: 0 14px;
  border-radius: 14px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: rgba(255, 255, 255, 0.86);
  font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.875rem;
  font-weight: 550;
  color: #0f172a;
  box-sizing: border-box;
  transition: border-color 0.16s ease, box-shadow 0.16s ease, background 0.16s ease;
  &:focus { outline: none; border-color: #6b8e7f; box-shadow: 0 0 0 4px rgba(107, 142, 127, 0.14); background: #fff; }
  &::placeholder { color: #94a3b8; }
`;

const GrantSearchInput = styled(GrantInput)`
  padding-left: 38px;
`;

const GrantTextarea = styled.textarea`
  min-height: 44px;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: rgba(255, 255, 255, 0.86);
  font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.45;
  color: #0f172a;
  resize: vertical;
  box-sizing: border-box;
  transition: border-color 0.16s ease, box-shadow 0.16s ease, background 0.16s ease;
  &:focus { outline: none; border-color: #6b8e7f; box-shadow: 0 0 0 4px rgba(107, 142, 127, 0.14); background: #fff; }
  &::placeholder { color: #64748b; }
`;

const GrantPrimary = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 22px;
  border: 0;
  border-radius: 11px;
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-dark) 100%);
  color: #fff;
  font-family: 'Inter', -apple-system, system-ui, sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(184, 149, 106, 0.15), 0 4px 12px -2px rgba(184, 149, 106, 0.30);
  transition: all 0.18s cubic-bezier(0.22, 1, 0.36, 1);

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, var(--primary-dark) 0%, #8A6E47 100%);
    box-shadow: 0 2px 4px rgba(184, 149, 106, 0.20), 0 6px 16px -2px rgba(184, 149, 106, 0.40);
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    cursor: not-allowed;
    background: #e5e7eb;
    color: #9ca3af;
    box-shadow: none;
  }
`;

const GrantSecondary = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 42px;
  padding: 0 18px;
  border: 1px solid rgba(15, 23, 42, 0.10);
  border-radius: 10px;
  background: #fff;
  color: var(--text-primary);
  font-family: 'Inter', -apple-system, system-ui, sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  letter-spacing: 0;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: var(--background-secondary);
    border-color: rgba(15, 23, 42, 0.18);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

/* ===== Wizard (minimalist) ===== */

const WizardBody = styled.div`
  padding: 2.25rem 2.25rem 0;

  @media (max-width: 768px) {
    padding: 1.75rem 1.5rem 0;
  }
`;

const Stepper = styled.ol`
  display: flex;
  align-items: center;
  list-style: none;
  margin: 0 0 36px;
  padding: 0;
  gap: 0;

  @media (max-width: 640px) {
    flex-wrap: wrap;
    row-gap: 8px;
  }
`;

const StepPill = styled.li`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-family: 'Inter', -apple-system, system-ui, sans-serif;
  font-size: 0.8125rem;
  font-weight: 500;
  letter-spacing: 0;
  text-transform: none;
  color: ${(p) => (p.$active ? 'var(--primary-dark)' : p.$done ? '#527366' : '#9ca3af')};
  cursor: ${(p) => (p.$done ? 'pointer' : 'default')};
  transition: color 0.18s ease;
  white-space: nowrap;

  & + & {
    margin-left: 12px;
    position: relative;
  }
  & + &::before {
    content: '';
    display: inline-block;
    width: 28px;
    height: 1px;
    background: ${(p) => (p.$done ? 'rgba(107, 142, 127, 0.40)' : 'rgba(15, 23, 42, 0.10)')};
    margin-right: 12px;
    vertical-align: middle;
    transition: background 0.18s ease;
  }

  &:hover {
    color: ${(p) => (p.$done ? 'var(--primary-color)' : undefined)};
  }
`;

const StepNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 999px;
  font-family: 'Inter', -apple-system, system-ui, sans-serif;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0;
  background: ${(p) =>
    p.$active
      ? 'linear-gradient(135deg, var(--primary-color), var(--primary-dark))'
      : p.$done
      ? 'linear-gradient(135deg, #6b8e7f, #527366)'
      : 'transparent'};
  border: 1px solid ${(p) =>
    p.$active ? 'transparent' : p.$done ? 'transparent' : 'rgba(15, 23, 42, 0.18)'};
  color: ${(p) => (p.$active || p.$done ? '#fff' : '#9ca3af')};
  box-shadow: ${(p) =>
    p.$active
      ? '0 0 0 4px rgba(184, 149, 106, 0.12), 0 2px 6px rgba(184, 149, 106, 0.22)'
      : p.$done
      ? '0 2px 4px rgba(82, 115, 102, 0.18)'
      : 'none'};
  transition: all 0.22s cubic-bezier(0.22, 1, 0.36, 1);
`;

const StepHeader = styled.div`
  margin-bottom: 28px;

  h3 {
    margin: 0;
    font-family: 'Inter', -apple-system, system-ui, sans-serif;
    font-size: clamp(1.375rem, 1vw + 1.1rem, 1.625rem);
    font-weight: 600;
    letter-spacing: -0.015em;
    color: var(--text-primary);
    line-height: 1.25;
    text-transform: none;
  }
  p {
    margin: 8px 0 0;
    font-size: 0.9375rem;
    color: var(--text-muted);
    line-height: 1.55;
    max-width: 60ch;
    font-weight: 400;
    letter-spacing: 0;
  }
`;

const PickerToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const SelectionCount = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(184, 149, 106, 0.10);
  border: 1px solid rgba(184, 149, 106, 0.18);
  font-family: 'Inter', -apple-system, system-ui, sans-serif;
  font-size: 0.8125rem;
  font-weight: 500;
  letter-spacing: 0;
  color: var(--primary-dark);
  font-variant-numeric: tabular-nums;

  strong { color: var(--primary-dark); font-weight: 700; }
`;

const SelectAllBtn = styled.button`
  background: transparent;
  border: 1px solid rgba(107, 142, 127, 0.30);
  padding: 5px 12px;
  border-radius: 999px;
  font-family: 'Inter', -apple-system, system-ui, sans-serif;
  font-size: 0.8125rem;
  font-weight: 500;
  letter-spacing: 0;
  color: #527366;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(107, 142, 127, 0.08);
    border-color: #6b8e7f;
    color: #3F6857;
  }
`;

const PickerList = styled.div`
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 14px;
  max-height: 380px;
  overflow-y: auto;
  background: #fff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
`;

const PickerRow = styled.label`
  /* Reset cascaded global label rules */
  text-transform: none;
  font-size: 1rem;
  font-weight: 400;
  letter-spacing: normal;
  line-height: 1.4;

  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 18px;
  cursor: pointer;
  border-bottom: 1px solid rgba(15, 23, 42, 0.05);
  user-select: none;
  background: ${(p) => (p.$checked ? 'linear-gradient(90deg, rgba(184, 149, 106, 0.07), rgba(184, 149, 106, 0.02))' : 'transparent')};
  transition: background 0.15s ease;

  &:last-child { border-bottom: 0; }
  &:hover { background: ${(p) => (p.$checked ? 'linear-gradient(90deg, rgba(184, 149, 106, 0.10), rgba(184, 149, 106, 0.04))' : 'rgba(247, 245, 241, 0.55)')}; }

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: ${(p) => (p.$checked ? 'var(--primary-color)' : 'transparent')};
    transition: background 0.18s ease;
  }

  input[type="checkbox"] {
    cursor: pointer;
    width: 16px;
    height: 16px;
    accent-color: var(--primary-color);
    flex-shrink: 0;
  }
`;

const PickerAvatar = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 999px;
  background: linear-gradient(135deg, rgba(184, 149, 106, 0.15), rgba(198, 171, 130, 0.12));
  color: var(--primary-dark);
  font-family: 'Inter', -apple-system, system-ui, sans-serif;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  flex-shrink: 0;
  border: 1px solid rgba(184, 149, 106, 0.22);
`;

const PickerItemIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: ${(p) => (p.$type === 'quiz'
    ? 'linear-gradient(135deg, rgba(107, 142, 127, 0.18), rgba(107, 142, 127, 0.10))'
    : 'linear-gradient(135deg, rgba(184, 149, 106, 0.18), rgba(184, 149, 106, 0.10))')};
  color: ${(p) => (p.$type === 'quiz' ? '#527366' : 'var(--primary-dark)')};
  flex-shrink: 0;
  border: 1px solid ${(p) => (p.$type === 'quiz' ? 'rgba(107, 142, 127, 0.22)' : 'rgba(184, 149, 106, 0.22)')};
`;

const PickerPrimary = styled.div`
  font-family: 'Inter', -apple-system, system-ui, sans-serif;
  font-size: 0.9375rem;
  font-weight: 500;
  letter-spacing: -0.005em;
  color: var(--text-primary);
  text-transform: none;
  line-height: 1.35;
`;

const PickerSecondary = styled.div`
  font-family: 'Inter', -apple-system, system-ui, sans-serif;
  font-size: 0.8125rem;
  font-weight: 400;
  color: var(--text-muted);
  margin-top: 3px;
  text-transform: none;
  letter-spacing: 0;
`;

const PickerTypeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-family: 'Inter', -apple-system, system-ui, sans-serif;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: ${(p) => (p.$type === 'quiz' ? 'rgba(107, 142, 127, 0.10)' : 'rgba(184, 149, 106, 0.12)')};
  color: ${(p) => (p.$type === 'quiz' ? '#527366' : 'var(--primary-dark)')};
  border: 1px solid ${(p) => (p.$type === 'quiz' ? 'rgba(107, 142, 127, 0.18)' : 'rgba(184, 149, 106, 0.20)')};
  margin-left: auto;
  flex-shrink: 0;
`;

const EmptyPicker = styled.div`
  padding: 48px 20px;
  text-align: center;
  font-family: 'Inter', -apple-system, system-ui, sans-serif;
  font-size: 0.875rem;
  color: var(--text-muted);
  font-weight: 400;

  svg {
    display: block;
    margin: 0 auto 12px;
    color: #d1d5db;
    opacity: 0.8;
  }
`;

const ReviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 0;
  margin-bottom: 28px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 14px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
`;

const ReviewCard = styled.div`
  position: relative;
  padding: 22px 24px;
  border-right: 1px solid rgba(15, 23, 42, 0.06);
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  transition: background 0.18s ease;

  &:last-child { border-right: 0; }
  &:hover { background: rgba(247, 245, 241, 0.45); }

  @media (max-width: 720px) {
    border-right: 0;
  }

  h4 {
    margin: 0 0 14px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: 'Inter', -apple-system, system-ui, sans-serif;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--primary-dark);
  }
  .value {
    font-family: 'Inter', -apple-system, system-ui, sans-serif;
    font-size: 1.625rem;
    font-weight: 600;
    letter-spacing: -0.015em;
    color: var(--text-primary);
    font-variant-numeric: tabular-nums;
    line-height: 1.1;
  }
  .meta {
    margin-top: 6px;
    font-size: 0.8125rem;
    color: var(--text-muted);
    font-weight: 400;
    letter-spacing: 0;
  }
  .courseText {
    font-family: 'Inter', -apple-system, system-ui, sans-serif;
    font-size: 1rem;
    font-weight: 600;
    letter-spacing: -0.005em;
    color: var(--text-primary);
    line-height: 1.4;
  }
`;

const ReviewList = styled.ul`
  margin: 12px 0 0;
  padding: 0;
  list-style: none;
  font-family: 'Inter', -apple-system, system-ui, sans-serif;
  font-size: 0.8125rem;
  color: var(--text-secondary);
  max-height: 110px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 4px;

  li {
    font-weight: 400;
    letter-spacing: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.45;
  }
  li.more {
    color: var(--text-muted);
    font-style: normal;
    font-weight: 500;
  }
`;

const ImpactBanner = styled.div`
  display: flex;
  align-items: baseline;
  gap: 20px;
  padding: 24px 26px;
  margin-bottom: 24px;
  border-radius: 14px;
  background: linear-gradient(135deg, rgba(184, 149, 106, 0.08) 0%, rgba(198, 171, 130, 0.05) 50%, rgba(107, 142, 127, 0.06) 100%);
  border: 1px solid rgba(184, 149, 106, 0.18);

  .total {
    font-family: 'Inter', -apple-system, system-ui, sans-serif;
    font-size: clamp(2.25rem, 3vw + 1rem, 3rem);
    font-weight: 600;
    letter-spacing: -0.02em;
    color: var(--primary-dark);
    line-height: 1;
    font-variant-numeric: tabular-nums;
  }
  .label {
    font-family: 'Inter', -apple-system, system-ui, sans-serif;
    font-size: 0.9375rem;
    font-weight: 400;
    color: var(--text-secondary);
    letter-spacing: 0;
    line-height: 1.5;
  }
  .label strong {
    color: var(--text-primary);
    font-weight: 600;
  }
`;

const NoteCard = styled.div`
  position: relative;
  padding: 18px 22px;
  border: 1px solid rgba(184, 149, 106, 0.18);
  border-left: 3px solid var(--primary-color);
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(247, 245, 241, 0.7), rgba(184, 149, 106, 0.03));
  font-family: 'Inter', -apple-system, system-ui, sans-serif;
  font-size: 0.9375rem;
  color: var(--text-primary);
  line-height: 1.6;
  font-weight: 400;
  letter-spacing: 0;

  h4 {
    margin: 0 0 8px;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--primary-dark);
  }
  .note {
    color: var(--text-secondary);
    font-style: italic;
  }
`;

const WizardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin: 32px -2.25rem 0;
  padding: 1.5rem 2.25rem;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  background: linear-gradient(180deg, transparent 0%, rgba(247, 245, 241, 0.6) 100%);

  .right {
    display: flex;
    gap: 10px;
  }

  @media (max-width: 768px) {
    margin: 32px -1.5rem 0;
    padding: 1.25rem 1.5rem;
  }
`;

const SuccessBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(56, 161, 105, 0.08) 0%, rgba(107, 142, 127, 0.10) 100%);
  border: 1px solid rgba(56, 161, 105, 0.22);
  color: #2F855A;
  font-family: 'Inter', -apple-system, system-ui, sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.5;
  margin-bottom: 24px;
  letter-spacing: 0;
  box-shadow: 0 1px 2px rgba(56, 161, 105, 0.06);

  svg { flex-shrink: 0; color: #38A169; }
`;

const CourseContextChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 12px 18px;
  margin-bottom: 28px;
  max-width: 100%;
  font-family: 'Inter', -apple-system, system-ui, sans-serif;
  background: linear-gradient(135deg, rgba(184, 149, 106, 0.07) 0%, rgba(107, 142, 127, 0.05) 100%);
  border: 1px solid rgba(184, 149, 106, 0.18);
  border-radius: 12px;

  .icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 9px;
    background: linear-gradient(135deg, var(--primary-color), var(--primary-dark));
    color: #fff;
    flex-shrink: 0;
    box-shadow: 0 1px 3px rgba(184, 149, 106, 0.30);
  }
  .meta {
    display: flex;
    flex-direction: column;
    gap: 1px;
    line-height: 1.2;
    min-width: 0;
  }
  .label {
    font-size: 0.7rem;
    font-weight: 500;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--primary-dark);
  }
  .name {
    font-size: 0.9375rem;
    font-weight: 600;
    letter-spacing: -0.005em;
    color: var(--text-primary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

/* ============================ Table Card ============================ */

const TableCard = styled.section`
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08), 0 0 0 0.5px rgba(0, 0, 0, 0.03);
  overflow: hidden;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--border-light);
  }
`;

const TableScroll = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  min-width: 920px;
`;

const Th = styled.th`
  text-align: left;
  font-weight: 700;
  color: var(--text-muted);
  font-size: 11px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 14px 18px;
  background: var(--background-secondary);
  border-bottom: 1px solid var(--border-color);
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 15px 18px;
  border-bottom: 1px solid var(--border-color);
  color: var(--text-primary);
  vertical-align: top;
  font-size: 13px;
`;

const Row = styled.tr`
  transition: background 0.15s ease;
  &:hover { background: var(--background-secondary); }
  &:last-child td { border-bottom: none; }
`;

const StudentCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Avatar = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(184, 149, 106, 0.12);
  color: var(--primary-dark);
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0;
  flex-shrink: 0;
  border: 1px solid rgba(184, 149, 106, 0.25);
`;

const StudentName = styled.div`
  font-weight: 600;
  color: var(--text-primary);
  font-size: 13px;
  line-height: 1.4;
`;

const StudentEmail = styled.div`
  font-size: 12px;
  color: var(--text-muted);
  margin-top: 2px;
`;

const SecondaryText = styled.span`
  display: block;
  font-size: 11.5px;
  color: var(--text-muted);
  margin-top: 2px;
  font-variant-numeric: tabular-nums;
`;

/* Traffic-light tones for status: green = approved, red = denied. */
const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  border: 1px solid transparent;
  white-space: nowrap;

  &.approved {
    background: #dcfce7;
    color: #15803d;
    border-color: #bbf7d0;
  }

  &.denied {
    background: #fee2e2;
    color: #dc2626;
    border-color: #fecaca;
  }
`;

const TypeChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: var(--background-secondary);
  color: var(--text-muted);
  border: 1px solid var(--border-color);
`;

const Justification = styled.div`
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.55;
  font-size: 12.5px;
  color: var(--text-muted);
  max-width: 360px;
`;

const ExpandBtn = styled.button`
  background: transparent;
  border: none;
  color: var(--primary-color);
  cursor: pointer;
  font-size: 11.5px;
  font-weight: 500;
  padding: 0;
  margin-top: 6px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: color 0.2s ease;

  &:hover { color: var(--primary-dark); }
`;

const Actions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const Btn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: 0.01em;
  border: 1px solid transparent;
  cursor: pointer;
  font-family: 'Inter', -apple-system, system-ui, sans-serif;
  transition: all 0.2s cubic-bezier(0.22, 1, 0.36, 1);

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ApproveBtn = styled(Btn)`
  background: var(--success-color);
  color: #fff;
  min-width: 110px;
  box-shadow: 0 2px 6px rgba(56, 161, 105, 0.15);

  &:hover:not(:disabled) {
    background: var(--success-dark);
    box-shadow: 0 4px 12px rgba(56, 161, 105, 0.25);
    transform: translateY(-1px);
  }
`;

const DenyBtn = styled(Btn)`
  background: transparent;
  color: var(--error-color);
  border-color: var(--error-light);
  min-width: 110px;

  &:hover:not(:disabled) {
    background: rgba(229, 62, 62, 0.05);
    border-color: var(--error-color);
  }
`;

const InlineMeta = styled.span`
  font-size: 11.5px;
  color: var(--text-muted);
`;

/* ============================ Empty state ============================ */

const Empty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4.5rem 2rem;
  color: #64748b;
`;

const EmptyIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 14px;
  background: rgba(184, 149, 106, 0.12);
  color: var(--primary-color);
  margin-bottom: 1rem;
`;

const EmptyTitle = styled.div`
  font-size: 1.0625rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
`;

const EmptyHint = styled.div`
  font-size: 0.875rem;
  color: var(--text-muted);
  max-width: 42ch;
  text-align: center;
  line-height: 1.6;
`;

/* ============================ Modals ============================ */

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1.5rem;
  animation: fadeIn 0.2s cubic-bezier(0.22, 1, 0.36, 1);

  @keyframes fadeIn {
    from { opacity: 0 }
    to { opacity: 1 }
  }

  @media (max-width: 768px) {
    padding: 1rem;
    align-items: flex-end;
  }
`;

const Modal = styled.div`
  background: var(--background-primary);
  border-radius: 14px;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15), 0 0 0 1px var(--border-color);
  font-family: 'Inter', -apple-system, system-ui, sans-serif;
  animation: slideUp 0.3s cubic-bezier(0.22, 1, 0.36, 1);

  @keyframes slideUp {
    from { transform: translateY(20px); opacity: 0 }
    to { transform: translateY(0); opacity: 1 }
  }

  @media (max-width: 768px) {
    border-radius: 16px 16px 0 0;
    max-width: 100%;
    max-height: 90vh;
    overflow-y: auto;
  }
`;

const ModalHead = styled.header`
  padding: 20px;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  gap: 12px;

  h3 {
    margin: 0;
    font-size: 1.125rem;
    font-weight: 700;
    color: var(--text-primary);
  }
`;

const ModalIconBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 9px;
  background: ${(p) => p.$tint || 'rgba(184, 149, 106, 0.12)'};
  color: ${(p) => p.$color || 'var(--primary-color)'};
  flex-shrink: 0;
`;

const ModalBody = styled.div`
  padding: 20px;
`;

const ModalFoot = styled.footer`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 20px;
  border-top: 1px solid var(--border-color);
  background: var(--background-secondary);
`;

const Label = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--text-muted);
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 13px;
  border-radius: 8px;
  border: 1px solid var(--border-color);
  font-size: 14px;
  font-family: 'Inter', -apple-system, system-ui, sans-serif;
  box-sizing: border-box;
  transition: all 0.2s ease;
  color: var(--text-primary);
  background: var(--background-primary);

  &:hover {
    border-color: var(--border-light);
  }

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(184, 149, 106, 0.15), 0 0 0 1px var(--primary-color);
  }
`;

const PresetRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
`;

const PresetChip = styled.button`
  padding: 6px 14px;
  border-radius: 999px;
  border: 1.5px solid ${(p) => (p.$active ? 'var(--primary-color)' : 'var(--border-color)')};
  background: ${(p) => (p.$active ? 'rgba(184, 149, 106, 0.15)' : 'var(--background-primary)')};
  color: ${(p) => (p.$active ? 'var(--primary-color)' : 'var(--text-secondary)')};
  font-family: 'Inter', -apple-system, system-ui, sans-serif;
  font-size: 12px;
  font-weight: ${(p) => (p.$active ? 600 : 500)};
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.22, 1, 0.36, 1);

  &:hover {
    background: ${(p) => (p.$active ? 'rgba(184, 149, 106, 0.25)' : 'var(--background-secondary)')};
    border-color: ${(p) => (p.$active ? 'var(--primary-color)' : 'var(--border-light)')};
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 90px;
  padding: 10px 13px;
  border-radius: 8px;
  border: 1px solid var(--border-color);
  font-size: 14px;
  font-family: 'Inter', -apple-system, system-ui, sans-serif;
  resize: vertical;
  box-sizing: border-box;
  color: var(--text-primary);
  background: var(--background-primary);
  transition: all 0.2s ease;
  line-height: 1.5;

  &:hover {
    border-color: var(--border-light);
  }

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(184, 149, 106, 0.15), 0 0 0 1px var(--primary-color);
  }
`;

const ErrMsg = styled.div`
  margin-top: 12px;
  padding: 12px 13px;
  background: rgba(229, 62, 62, 0.08);
  border: 1px solid var(--error-light);
  border-radius: 8px;
  color: var(--error-color);
  font-size: 13px;
  font-weight: 500;
`;

const HistoryBox = styled.section`
  margin-top: 16px;
  padding: 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  background: rgba(248, 247, 244, 0.55);
`;

const HistoryTitle = styled.div`
  font-size: 11px;
  font-weight: 650;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-muted);
  margin-bottom: 8px;
`;

const HistoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 180px;
  overflow: auto;
`;

const HistoryItem = styled.div`
  padding: 9px 10px;
  border-radius: 8px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
`;

const HistoryItemTop = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--text-primary);
`;

const HistoryMeta = styled.div`
  margin-top: 3px;
  font-size: 11.5px;
  color: var(--text-muted);
  line-height: 1.45;
`;

const HistoryEmpty = styled.div`
  font-size: 12.5px;
  color: var(--text-muted);
  line-height: 1.45;
`;

const HintText = styled.p`
  font-size: 13px;
  color: var(--text-muted);
  margin: 0 0 12px 0;
  line-height: 1.5;
`;

const HighlightText = styled.span`
  color: var(--text-primary, #1a202c);
  font-weight: 650;
`;

const CancelBtn = styled(Btn)`
  background: transparent;
  color: var(--text-secondary);
  border-color: var(--border-color);

  &:hover:not(:disabled) {
    background: var(--background-secondary);
    border-color: var(--border-light);
    color: var(--text-primary);
  }
`;

const ConfirmDenyBtn = styled(Btn)`
  background: var(--error-color);
  color: #fff;
  box-shadow: 0 2px 6px rgba(229, 62, 62, 0.15);

  &:hover:not(:disabled) {
    background: var(--error-dark);
    box-shadow: 0 4px 12px rgba(229, 62, 62, 0.25);
    transform: translateY(-1px);
  }
`;

/* ============================ Helpers ============================ */

function formatDateTime(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch { return String(value); }
}

function initials(first, last) {
  const a = (first || '').trim()[0] || '';
  const b = (last  || '').trim()[0] || '';
  return (a + b).toUpperCase() || '·';
}

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.32, delay, ease: [0.22, 1, 0.36, 1] },
});

/* ============================ Modals ============================ */

function ApproveModal({ open, onClose, onConfirm, request }) {
  const [hours, setHours] = useState(48);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyErr, setHistoryErr] = useState('');

  useEffect(() => {
    if (!open) return;
    setHours(48);
    setNote('');
    setErr('');
    setHistory([]);
    setHistoryErr('');

    if (!request?.student_id || !request?.course_id) return;
    let cancelled = false;
    setHistoryLoading(true);
    apiService.getExtensionHistory({
      studentId: request.student_id,
      courseId: request.course_id,
      excludeId: request.id,
    })
      .then((res) => {
        if (!cancelled) setHistory(res?.history || []);
      })
      .catch((e) => {
        if (!cancelled) setHistoryErr(e?.data?.message || e?.message || 'Failed to load extension history.');
      })
      .finally(() => {
        if (!cancelled) setHistoryLoading(false);
      });

    return () => { cancelled = true; };
  }, [open, request]);
  if (!open) return null;

  const submit = async () => {
    setErr('');
    const n = Number(hours);
    if (!Number.isFinite(n) || n <= 0) { setErr('Enter a positive number of hours.'); return; }
    setSubmitting(true);
    try { await onConfirm({ durationHours: n, note: note.trim() || undefined }); }
    catch (e) { setErr(e?.errorData?.message || e?.message || 'Failed to approve.'); }
    finally { setSubmitting(false); }
  };

  return (
    <Overlay onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <Modal>
        <ModalHead>
          <ModalIconBadge $tint="rgba(107, 142, 127, 0.16)" $color="#466B5E"><FiCheck size={16} /></ModalIconBadge>
          <h3>Approve Extension</h3>
        </ModalHead>
        <ModalBody>
          <HintText>
            Grant <HighlightText>{request?.student_first_name} {request?.student_last_name}</HighlightText> access to{' '}
            <HighlightText>“{request?.item_title}”</HighlightText> for a limited time.
          </HintText>
          <Label htmlFor="approve-hours">Grant access duration (hours):</Label>
          <Input
            id="approve-hours" type="number" min="1" max="2160" value={hours}
            onChange={(e) => setHours(e.target.value)}
          />
          <PresetRow>
            {[
              { label: '1 day',   h: 24  },
              { label: '2 days',  h: 48  },
              { label: '3 days',  h: 72  },
              { label: '1 week',  h: 168 },
              { label: '2 weeks', h: 336 },
              { label: '1 month', h: 720 },
            ].map((p) => (
              <PresetChip
                key={p.h}
                type="button"
                onClick={() => setHours(p.h)}
                $active={Number(hours) === p.h}
              >
                {p.label}
              </PresetChip>
            ))}
          </PresetRow>
          <div style={{ height: 14 }} />
          <Label htmlFor="approve-note">Note to student (optional)</Label>
          <Textarea id="approve-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
          <HistoryBox>
            <HistoryTitle>Course Extension Request History</HistoryTitle>
            {historyLoading ? (
              <HistoryEmpty>Loading extension history…</HistoryEmpty>
            ) : historyErr ? (
              <HistoryEmpty>{historyErr}</HistoryEmpty>
            ) : history.length === 0 ? (
              <HistoryEmpty>No previously approved extensions for this student in this course.</HistoryEmpty>
            ) : (
              <HistoryList>
                {history.map((h) => (
                  <HistoryItem key={h.id}>
                    <HistoryItemTop>
                      <span>{h.item_title || `#${h.item_id}`}</span>
                      <TypeChip>{h.item_type}</TypeChip>
                    </HistoryItemTop>
                    <HistoryMeta>
                      Approved {formatDateTime(h.reviewed_at || h.requested_at)}
                      {h.extension_duration_hours ? ` for ${h.extension_duration_hours} hours` : ''}
                      {h.access_expires_at ? ` · access until ${formatDateTime(h.access_expires_at)}` : ''}
                    </HistoryMeta>
                    {(h.review_note || h.justification) && (
                      <HistoryMeta>{h.review_note || h.justification}</HistoryMeta>
                    )}
                  </HistoryItem>
                ))}
              </HistoryList>
            )}
          </HistoryBox>
          {err && <ErrMsg>{err}</ErrMsg>}
        </ModalBody>
        <ModalFoot>
          <CancelBtn onClick={onClose} disabled={submitting}>Cancel</CancelBtn>
          <ApproveBtn onClick={submit} disabled={submitting}>
            {submitting ? 'Approving…' : 'Approve Extension'}
          </ApproveBtn>
        </ModalFoot>
      </Modal>
    </Overlay>
  );
}

function DenyModal({ open, onClose, onConfirm, request }) {
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { if (open) { setNote(''); setErr(''); } }, [open]);
  if (!open) return null;

  const submit = async () => {
    setSubmitting(true); setErr('');
    try { await onConfirm({ note: note.trim() || undefined }); }
    catch (e) { setErr(e?.errorData?.message || e?.message || 'Failed to deny.'); }
    finally { setSubmitting(false); }
  };

  return (
    <Overlay onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <Modal>
        <ModalHead>
          <ModalIconBadge $tint="rgba(178, 125, 92, 0.14)" $color="#8C5638"><FiX size={16} /></ModalIconBadge>
          <h3>Deny Extension</h3>
        </ModalHead>
        <ModalBody>
          <HintText>
            Deny {request?.student_first_name} {request?.student_last_name}’s request for
            “{request?.item_title}”.
          </HintText>
          <Label htmlFor="deny-note">Reason for denial (optional)</Label>
          <Textarea id="deny-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
          {err && <ErrMsg>{err}</ErrMsg>}
        </ModalBody>
        <ModalFoot>
          <CancelBtn onClick={onClose} disabled={submitting}>Cancel</CancelBtn>
          <ConfirmDenyBtn onClick={submit} disabled={submitting}>
            {submitting ? 'Denying…' : 'Deny Request'}
          </ConfirmDenyBtn>
        </ModalFoot>
      </Modal>
    </Overlay>
  );
}

/* ============================ Page ============================ */

export default function AdminExtensions() {
  const [allRequests, setAllRequests] = useState([]);
  const [grantOptions, setGrantOptions] = useState({ courses: [], studentsByCourse: {}, assignmentsByCourse: {}, quizzesByCourse: {} });
  const [activeSection, setActiveSection] = useState('requests');
  const [loading, setLoading] = useState(true);
  const [grantLoading, setGrantLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [courseFilter, setCourseFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [expanded, setExpanded] = useState({});
  const [approveTarget, setApproveTarget] = useState(null);
  const [denyTarget, setDenyTarget] = useState(null);
  // Grant wizard state — multi-select students + multi-select items across steps
  const [grantStep, setGrantStep] = useState(1);
  const [grantCourseId, setGrantCourseId] = useState('');
  const [grantStudentIds, setGrantStudentIds] = useState([]); // string[]
  const [grantItemKeys, setGrantItemKeys] = useState([]);     // ["assignment-12", "quiz-5"]
  const [grantStudentSearch, setGrantStudentSearch] = useState('');
  const [grantItemSearch, setGrantItemSearch] = useState('');
  const [grantDuration, setGrantDuration] = useState('72');
  const [grantNote, setGrantNote] = useState('');
  const [grantSubmitting, setGrantSubmitting] = useState(false);
  const [grantError, setGrantError] = useState('');
  const [grantSuccess, setGrantSuccess] = useState('');

  // Server-side filtering for course/date, client-side for status + search so
  // counts on the stat tabs always reflect the same underlying dataset.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.listExtensions({
        courseId: courseFilter || undefined,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
      });
      if (res?.success) setAllRequests(res.requests || []);
    } catch (err) {
      logger.error('Failed to load extension requests', { err });
      setAllRequests([]);
    } finally {
      setLoading(false);
    }
  }, [courseFilter, fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  const loadGrantOptions = useCallback(async () => {
    setGrantLoading(true);
    try {
      const res = await apiService.getExtensionGrantOptions();
      if (res?.success) setGrantOptions({
        courses: res.courses || [],
        studentsByCourse: res.studentsByCourse || {},
        assignmentsByCourse: res.assignmentsByCourse || {},
        quizzesByCourse: res.quizzesByCourse || {},
      });
    } catch (err) {
      logger.error('Failed to load extension grant options', { err });
      setGrantOptions({ courses: [], studentsByCourse: {}, assignmentsByCourse: {}, quizzesByCourse: {} });
    } finally {
      setGrantLoading(false);
    }
  }, []);

  useEffect(() => { loadGrantOptions(); }, [loadGrantOptions]);

  const counts = useMemo(() => {
    const tally = { all: allRequests.length, pending: 0, approved: 0, denied: 0 };
    for (const r of allRequests) tally[r.status] = (tally[r.status] || 0) + 1;
    return tally;
  }, [allRequests]);

  const visible = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return allRequests.filter((r) => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (!term) return true;
      const hay = [
        r.student_first_name, r.student_last_name, r.student_email,
        r.course_name, r.course_code, r.item_title, r.justification,
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(term);
    });
  }, [allRequests, statusFilter, searchTerm]);

  const courseOptions = useMemo(() => {
    const m = new Map();
    allRequests.forEach((r) => {
      if (r.course_id && !m.has(r.course_id)) {
        m.set(r.course_id, {
          id: r.course_id,
          label: `${r.course_code || ''}${r.course_code ? ' · ' : ''}${r.course_name || `Course ${r.course_id}`}`,
        });
      }
    });
    return Array.from(m.values());
  }, [allRequests]);

  const handleApprove = async ({ durationHours, note }) => {
    await apiService.approveExtension(approveTarget.id, { durationHours, note });
    setApproveTarget(null);
    load();
  };
  const handleDeny = async ({ note }) => {
    await apiService.denyExtension(denyTarget.id, { note });
    setDenyTarget(null);
    load();
  };

  const grantStudents = useMemo(
    () => (grantCourseId ? (grantOptions.studentsByCourse[grantCourseId] || []) : []),
    [grantCourseId, grantOptions.studentsByCourse]
  );
  const grantAssignments = useMemo(
    () => (grantCourseId ? (grantOptions.assignmentsByCourse[grantCourseId] || []) : []),
    [grantCourseId, grantOptions.assignmentsByCourse]
  );
  const grantQuizzes = useMemo(
    () => (grantCourseId ? (grantOptions.quizzesByCourse[grantCourseId] || []) : []),
    [grantCourseId, grantOptions.quizzesByCourse]
  );

  // Combine assignments + quizzes into a single list of selectable items with a unified key.
  const allGrantItems = useMemo(() => [
    ...grantAssignments.map((a) => ({ key: `assignment-${a.id}`, id: a.id, type: 'assignment', title: a.title })),
    ...grantQuizzes.map((q) => ({ key: `quiz-${q.id}`, id: q.id, type: 'quiz', title: q.title })),
  ], [grantAssignments, grantQuizzes]);

  const visibleGrantStudents = useMemo(() => {
    const term = grantStudentSearch.trim().toLowerCase();
    if (!term) return grantStudents;
    return grantStudents.filter((s) => {
      const hay = [s.firstName, s.lastName, s.email].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(term);
    });
  }, [grantStudents, grantStudentSearch]);

  const visibleGrantItems = useMemo(() => {
    const term = grantItemSearch.trim().toLowerCase();
    if (!term) return allGrantItems;
    return allGrantItems.filter((item) => (item.title || '').toLowerCase().includes(term));
  }, [allGrantItems, grantItemSearch]);

  const selectedGrantStudents = useMemo(
    () => grantStudents.filter((s) => grantStudentIds.includes(String(s.id))),
    [grantStudents, grantStudentIds]
  );
  const selectedGrantItems = useMemo(
    () => allGrantItems.filter((it) => grantItemKeys.includes(it.key)),
    [allGrantItems, grantItemKeys]
  );

  const handleGrantCourseChange = (value) => {
    setGrantCourseId(value);
    setGrantStudentIds([]);
    setGrantItemKeys([]);
    setGrantStudentSearch('');
    setGrantItemSearch('');
    setGrantError('');
    setGrantSuccess('');
  };

  const toggleGrantStudent = (id) => {
    const key = String(id);
    setGrantStudentIds((prev) => prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]);
  };
  const toggleAllVisibleStudents = () => {
    const visibleIds = visibleGrantStudents.map((s) => String(s.id));
    const allSelected = visibleIds.every((id) => grantStudentIds.includes(id));
    setGrantStudentIds((prev) => allSelected
      ? prev.filter((id) => !visibleIds.includes(id))
      : Array.from(new Set([...prev, ...visibleIds]))
    );
  };

  const toggleGrantItem = (itemKey) => {
    setGrantItemKeys((prev) => prev.includes(itemKey) ? prev.filter((x) => x !== itemKey) : [...prev, itemKey]);
  };
  const toggleAllVisibleItems = () => {
    const visibleKeys = visibleGrantItems.map((i) => i.key);
    const allSelected = visibleKeys.every((k) => grantItemKeys.includes(k));
    setGrantItemKeys((prev) => allSelected
      ? prev.filter((k) => !visibleKeys.includes(k))
      : Array.from(new Set([...prev, ...visibleKeys]))
    );
  };

  const canAdvanceStep = (step) => {
    if (step === 1) return !!grantCourseId;
    if (step === 2) return grantStudentIds.length > 0;
    if (step === 3) return grantItemKeys.length > 0;
    if (step === 4) return Number(grantDuration) > 0;
    return false;
  };

  const resetWizard = () => {
    setGrantStep(1);
    setGrantCourseId('');
    setGrantStudentIds([]);
    setGrantItemKeys([]);
    setGrantStudentSearch('');
    setGrantItemSearch('');
    setGrantDuration('72');
    setGrantNote('');
    setGrantError('');
    setGrantSuccess('');
  };

  const handleGrant = async () => {
    if (grantSubmitting) return;
    if (!canAdvanceStep(1) || !canAdvanceStep(2) || !canAdvanceStep(3) || !canAdvanceStep(4)) return;

    setGrantSubmitting(true);
    setGrantError('');
    setGrantSuccess('');
    try {
      const res = await apiService.request('/extensions/grant-bulk', {
        method: 'POST',
        body: JSON.stringify({
          courseId: grantCourseId,
          studentIds: grantStudentIds,
          items: selectedGrantItems.map((it) => ({ itemType: it.type, itemId: it.id })),
          durationHours: Number(grantDuration),
          note: grantNote.trim() || undefined,
        }),
      });
      const successCount = res?.successCount ?? 0;
      const errs = Array.isArray(res?.errors) ? res.errors : [];
      setGrantSuccess(`Granted ${successCount} extension(s) to ${grantStudentIds.length} student(s) across ${grantItemKeys.length} item(s).${errs.length ? ` ${errs.length} issue(s) — see console.` : ''}`);
      if (errs.length) logger.warn('Bulk grant partial errors', { errors: errs });
      await Promise.all([load(), loadGrantOptions()]);
      setStatusFilter('approved');
    } catch (err) {
      setGrantError(err?.errorData?.message || err?.data?.message || err?.message || 'Failed to grant extensions.');
    } finally {
      setGrantSubmitting(false);
    }
  };

  const tabs = [
    { key: 'all',      label: 'All requests', caption: 'Every request on record', icon: <FiInbox size={12} />,       tint: 'rgba(184, 149, 106, 0.12)', color: '#B8956A' },
    { key: 'pending',  label: 'Pending',      caption: 'Awaiting review',         icon: <FiClock size={12} />,       tint: 'rgba(168, 146, 84, 0.12)',  color: '#9A8344' },
    { key: 'approved', label: 'Approved',     caption: 'Currently active or used', icon: <FiCheckCircle size={12} />, tint: 'rgba(107, 142, 127, 0.14)', color: '#5C8676' },
    { key: 'denied',   label: 'Denied',       caption: 'Recently rejected',       icon: <FiXCircle size={12} />,     tint: 'rgba(178, 125, 92, 0.14)',  color: '#A86A4A' },
  ];

  const formatDateForHero = () =>
    new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

  return (
    <Page>
      <Hero>
        <HeroEyebrow>{formatDateForHero()}</HeroEyebrow>
        <HeroTitle>
          <HeroTitleWrap>Extension</HeroTitleWrap> Requests
        </HeroTitle>
        <HeroSub>
          Review and respond to student requests to access work in inactive or ended courses.
        </HeroSub>
      </Hero>

      <SectionNav aria-label="Extension sections">
        <SectionNavItem type="button" onClick={() => setActiveSection('requests')} $active={activeSection === 'requests'}>
          <FiClock size={16} /> Pending requests
        </SectionNavItem>
        <SectionNavItem type="button" onClick={() => setActiveSection('grant')} $active={activeSection === 'grant'}>
          <FiPlus size={16} /> Grant extension
        </SectionNavItem>
      </SectionNav>

      <ContentShell>
        <MainStack>
      {activeSection === 'grant' && <GrantCard id="grant-extension">
        <GrantHead>
          <GrantTitle>
            <div>
              <h2>Grant an extension</h2>
              <p>Grant one or many extensions across students and items.</p>
            </div>
          </GrantTitle>
        </GrantHead>

        <WizardBody>
        <Stepper aria-label="Wizard progress">
          {[
            { n: 1, label: 'Course' },
            { n: 2, label: 'Students' },
            { n: 3, label: 'Items' },
            { n: 4, label: 'Details' },
            { n: 5, label: 'Review' },
          ].map((s) => {
            const active = grantStep === s.n;
            const done = grantStep > s.n;
            return (
              <StepPill
                key={s.n}
                $active={active}
                $done={done}
                onClick={() => { if (done) setGrantStep(s.n); }}
              >
                <StepNumber $active={active} $done={done}>{done ? <FiCheck size={14} /> : s.n}</StepNumber>
                {s.label}
              </StepPill>
            );
          })}
        </Stepper>

        {grantSuccess && (
          <SuccessBanner>
            <FiCheckCircle size={18} />
            <div>{grantSuccess}</div>
          </SuccessBanner>
        )}

        {/* Step 1: Course */}
        {grantStep === 1 && (
          <div>
            <StepHeader>
              <h3>Which course is this for?</h3>
              <p>Pick the course. You'll choose students and items inside that course next.</p>
            </StepHeader>
            <GrantGrid style={{ padding: 0 }}>
              <GrantField $span={12}>
                <GrantLabel htmlFor="grant-course">Course</GrantLabel>
                <GrantControlWrap>
                  <GrantSelect id="grant-course" value={grantCourseId} onChange={(e) => handleGrantCourseChange(e.target.value)} disabled={grantLoading}>
                    <option value="">Select course</option>
                    {grantOptions.courses.map((c) => (
                      <option key={c.id} value={c.id}>{c.code ? `${c.code} · ` : ''}{c.name}</option>
                    ))}
                  </GrantSelect>
                  <GrantChevron><FiChevronDown size={16} /></GrantChevron>
                </GrantControlWrap>
              </GrantField>
            </GrantGrid>
          </div>
        )}

        {grantStep > 1 && grantStep < 5 && (() => {
          const c = grantOptions.courses.find((x) => String(x.id) === String(grantCourseId));
          if (!c) return null;
          return (
            <CourseContextChip>
              <span className="icon"><FiBook size={16} /></span>
              <span className="meta">
                <span className="label">Course</span>
                <span className="name">{c.code ? `${c.code} · ` : ''}{c.name}</span>
              </span>
            </CourseContextChip>
          );
        })()}

        {/* Step 2: Students */}
        {grantStep === 2 && (
          <div>
            <StepHeader>
              <h3>Select students</h3>
              <p>Choose one or more students. The same extension applies to everyone you pick.</p>
            </StepHeader>
            <PickerToolbar>
              <GrantControlWrap style={{ flex: 1, minWidth: 220 }}>
                <GrantSearchIcon><FiSearch size={15} /></GrantSearchIcon>
                <GrantSearchInput
                  type="search"
                  placeholder="Search student by name or email"
                  value={grantStudentSearch}
                  onChange={(e) => setGrantStudentSearch(e.target.value)}
                />
              </GrantControlWrap>
              <SelectionCount><strong>{grantStudentIds.length}</strong> selected</SelectionCount>
              {visibleGrantStudents.length > 0 && (
                <SelectAllBtn type="button" onClick={toggleAllVisibleStudents}>
                  {visibleGrantStudents.every((s) => grantStudentIds.includes(String(s.id))) ? 'Clear visible' : 'Select all visible'}
                </SelectAllBtn>
              )}
            </PickerToolbar>
            <PickerList>
              {visibleGrantStudents.length === 0 ? (
                <EmptyPicker><FiUsers size={28} />{grantStudents.length === 0 ? 'No active students enrolled in this course.' : 'No students match your search.'}</EmptyPicker>
              ) : visibleGrantStudents.map((s) => {
                const id = String(s.id);
                const checked = grantStudentIds.includes(id);
                const name = [s.firstName, s.lastName].filter(Boolean).join(' ') || s.email;
                const init = initials(s.firstName, s.lastName) || (s.email || '?').slice(0, 2).toUpperCase();
                return (
                  <PickerRow key={id} $checked={checked}>
                    <input type="checkbox" checked={checked} onChange={() => toggleGrantStudent(id)} />
                    <PickerAvatar>{init}</PickerAvatar>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <PickerPrimary>{name}</PickerPrimary>
                      <PickerSecondary>{s.email}</PickerSecondary>
                    </div>
                  </PickerRow>
                );
              })}
            </PickerList>
          </div>
        )}

        {/* Step 3: Items */}
        {grantStep === 3 && (
          <div>
            <StepHeader>
              <h3>Select items</h3>
              <p>Pick any mix of assignments and quizzes. Each student gets the extension for each item.</p>
            </StepHeader>
            <PickerToolbar>
              <GrantControlWrap style={{ flex: 1, minWidth: 220 }}>
                <GrantSearchIcon><FiSearch size={15} /></GrantSearchIcon>
                <GrantSearchInput
                  type="search"
                  placeholder="Search assignments and quizzes"
                  value={grantItemSearch}
                  onChange={(e) => setGrantItemSearch(e.target.value)}
                />
              </GrantControlWrap>
              <SelectionCount><strong>{grantItemKeys.length}</strong> selected</SelectionCount>
              {visibleGrantItems.length > 0 && (
                <SelectAllBtn type="button" onClick={toggleAllVisibleItems}>
                  {visibleGrantItems.every((i) => grantItemKeys.includes(i.key)) ? 'Clear visible' : 'Select all visible'}
                </SelectAllBtn>
              )}
            </PickerToolbar>
            <PickerList>
              {visibleGrantItems.length === 0 ? (
                <EmptyPicker><FiFileText size={28} />{allGrantItems.length === 0 ? 'No assignments or quizzes available for this course.' : 'No items match your search.'}</EmptyPicker>
              ) : visibleGrantItems.map((it) => {
                const checked = grantItemKeys.includes(it.key);
                return (
                  <PickerRow key={it.key} $checked={checked}>
                    <input type="checkbox" checked={checked} onChange={() => toggleGrantItem(it.key)} />
                    <PickerItemIcon $type={it.type}>
                      {it.type === 'quiz' ? <FiCheckCircle size={16} /> : <FiFileText size={16} />}
                    </PickerItemIcon>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <PickerPrimary style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.title}</PickerPrimary>
                    </div>
                    <PickerTypeBadge $type={it.type}>{it.type === 'quiz' ? 'Quiz / Exam' : 'Assignment'}</PickerTypeBadge>
                  </PickerRow>
                );
              })}
            </PickerList>
          </div>
        )}

        {/* Step 4: Details */}
        {grantStep === 4 && (
          <div>
            <StepHeader>
              <h3>Extension details</h3>
              <p>How long should the extension last? Add an optional note shown to the student.</p>
            </StepHeader>
            <GrantGrid style={{ padding: 0 }}>
              <GrantField $span={4}>
                <GrantLabel htmlFor="grant-duration">Duration (hours)</GrantLabel>
                <GrantInput
                  id="grant-duration"
                  type="number"
                  min="1"
                  max="2160"
                  value={grantDuration}
                  onChange={(e) => setGrantDuration(e.target.value)}
                />
              </GrantField>
              <GrantField $span={12}>
                <GrantLabel htmlFor="grant-note">Note (optional)</GrantLabel>
                <GrantTextarea
                  id="grant-note"
                  value={grantNote}
                  onChange={(e) => setGrantNote(e.target.value)}
                  placeholder="Reason or instructions for the students"
                  maxLength={500}
                />
              </GrantField>
            </GrantGrid>
          </div>
        )}

        {/* Step 5: Review */}
        {grantStep === 5 && (
          <div>
            <StepHeader>
              <h3>Review</h3>
              <p>Take a final look before granting access.</p>
            </StepHeader>

            <ImpactBanner>
              <div className="total">{grantStudentIds.length * grantItemKeys.length}</div>
              <div className="label">
                extension{(grantStudentIds.length * grantItemKeys.length) === 1 ? '' : 's'} will be granted<br/>
                <strong>{grantStudentIds.length}</strong> student{grantStudentIds.length === 1 ? '' : 's'} × <strong>{grantItemKeys.length}</strong> item{grantItemKeys.length === 1 ? '' : 's'}
              </div>
            </ImpactBanner>

            <ReviewGrid>
              <ReviewCard>
                <h4>Course</h4>
                <div className="courseText">
                  {(() => {
                    const c = grantOptions.courses.find((x) => String(x.id) === String(grantCourseId));
                    return c ? `${c.code ? c.code + ' · ' : ''}${c.name}` : '—';
                  })()}
                </div>
              </ReviewCard>
              <ReviewCard>
                <h4>Duration</h4>
                <div className="value">{Number(grantDuration)}<span style={{ fontSize: '0.9375rem', fontWeight: 400, color: 'var(--text-muted)', marginLeft: 6, letterSpacing: '-0.01em' }}>hrs</span></div>
                <div className="meta">≈ {Math.round(Number(grantDuration) / 24 * 10) / 10} day{Math.round(Number(grantDuration) / 24 * 10) / 10 === 1 ? '' : 's'}</div>
              </ReviewCard>
              <ReviewCard>
                <h4>Students</h4>
                <div className="value">{grantStudentIds.length}</div>
                <ReviewList>
                  {selectedGrantStudents.slice(0, 6).map((s) => (
                    <li key={s.id}>{[s.firstName, s.lastName].filter(Boolean).join(' ') || s.email}</li>
                  ))}
                  {selectedGrantStudents.length > 6 && <li className="more">+ {selectedGrantStudents.length - 6} more</li>}
                </ReviewList>
              </ReviewCard>
              <ReviewCard>
                <h4>Items</h4>
                <div className="value">{grantItemKeys.length}</div>
                <ReviewList>
                  {selectedGrantItems.slice(0, 6).map((it) => (
                    <li key={it.key}>{it.title}</li>
                  ))}
                  {selectedGrantItems.length > 6 && <li className="more">+ {selectedGrantItems.length - 6} more</li>}
                </ReviewList>
              </ReviewCard>
            </ReviewGrid>

            {grantNote.trim() && (
              <NoteCard>
                <h4>Note to students</h4>
                <div className="note">"{grantNote.trim()}"</div>
              </NoteCard>
            )}
          </div>
        )}

        {grantError && <ErrMsg style={{ marginTop: 16 }}>{grantError}</ErrMsg>}

        <WizardFooter>
          <GrantSecondary
            type="button"
            onClick={() => grantStep > 1 ? setGrantStep(grantStep - 1) : resetWizard()}
            disabled={grantSubmitting}
          >
            <FiArrowLeft size={14} /> {grantStep === 1 ? 'Reset' : 'Back'}
          </GrantSecondary>
          <div className="right">
            {grantStep < 5 ? (
              <GrantPrimary
                type="button"
                onClick={() => setGrantStep(grantStep + 1)}
                disabled={!canAdvanceStep(grantStep) || grantLoading}
              >
                Next <FiArrowRight size={14} />
              </GrantPrimary>
            ) : (
              <GrantPrimary
                type="button"
                onClick={handleGrant}
                disabled={grantSubmitting || grantLoading || grantStudentIds.length === 0 || grantItemKeys.length === 0 || Number(grantDuration) <= 0}
              >
                <FiCheck size={13} /> {grantSubmitting ? 'Granting…' : `Grant ${grantStudentIds.length * grantItemKeys.length} extension(s)`}
              </GrantPrimary>
            )}
          </div>
        </WizardFooter>
        </WizardBody>
      </GrantCard>}

      {activeSection === 'requests' && <section id="extension-requests">
      {/* Status tabs as KPI stat cards */}
      <StatGrid role="tablist" aria-label="Filter by status">
        {tabs.map((t, i) => {
          const active = statusFilter === t.key;
          return (
            <StatCard
              key={t.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setStatusFilter(t.key)}
              style={{
                borderColor: active ? t.color : undefined,
                boxShadow: active
                  ? `0 6px 20px rgba(0,0,0,0.06), 0 0 0 1px ${t.color}`
                  : undefined,
              }}
              {...fadeUp(i * 0.04)}
            >
              <StatStrip $color={t.color} />
              <StatLabel>
                <StatSwatch $tint={t.tint} $color={t.color}>{t.icon}</StatSwatch>
                {t.label}
              </StatLabel>
              <StatValue>{counts[t.key] ?? 0}</StatValue>
            </StatCard>
          );
        })}
      </StatGrid>

      {/* Filters */}
      <FiltersBar>
        <SearchWrap>
          <SearchIcon><FiSearch size={14} /></SearchIcon>
          <SearchInput
            type="text"
            placeholder="Search student, course, item, justification…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchWrap>
        <SelectWrap>
          <SelectIcon><FiBook size={14} /></SelectIcon>
          <Select value={courseFilter} onChange={(e) => setCourseFilter(e.target.value)} aria-label="Course">
            <option value="">All courses</option>
            {courseOptions.map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </Select>
          <SelectChevron><FiChevronDown size={16} /></SelectChevron>
        </SelectWrap>
        <DateFilterWrap>
          <DateLabelInline htmlFor="from-date">From</DateLabelInline>
          <DateInputWrap>
            <DateInputIcon><FiCalendar size={14} /></DateInputIcon>
            <DateInput id="from-date" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} aria-label="From date" />
          </DateInputWrap>
        </DateFilterWrap>
        <DateFilterWrap>
          <DateLabelInline htmlFor="to-date">To</DateLabelInline>
          <DateInputWrap>
            <DateInputIcon><FiCalendar size={14} /></DateInputIcon>
            <DateInput id="to-date" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} aria-label="To date" />
          </DateInputWrap>
        </DateFilterWrap>
      </FiltersBar>

      {/* Table */}
      <TableCard>
        <TableScroll>
          <Table>
            <thead>
              <tr>
                <Th>Student</Th>
                <Th>Course / Item</Th>
                <Th>Type</Th>
                <Th>Justification</Th>
                <Th>Requested</Th>
                <Th>Status</Th>
                <Th style={{ textAlign: 'right' }}>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><Td colSpan={7}>
                  <Empty>
                    <EmptyIcon><FiClock size={22} /></EmptyIcon>
                    <EmptyTitle>Loading…</EmptyTitle>
                  </Empty>
                </Td></tr>
              ) : visible.length === 0 ? (
                <tr><Td colSpan={7}>
                  <Empty>
                    <EmptyIcon><FiInbox size={22} /></EmptyIcon>
                    <EmptyTitle>No extension requests match your filters</EmptyTitle>
                    <EmptyHint>Try clearing the search or selecting a different status above to see more results.</EmptyHint>
                  </Empty>
                </Td></tr>
              ) : visible.map((r) => {
                const isExpanded = expanded[r.id];
                const long = (r.justification || '').length > 140;
                const justText = isExpanded || !long
                  ? r.justification
                  : `${(r.justification || '').slice(0, 140)}…`;
                return (
                  <Row key={r.id}>
                    <Td>
                      <StudentCell>
                        <Avatar>{initials(r.student_first_name, r.student_last_name)}</Avatar>
                        <div>
                          <StudentName>{r.student_first_name} {r.student_last_name}</StudentName>
                          <StudentEmail>{r.student_email}</StudentEmail>
                        </div>
                      </StudentCell>
                    </Td>
                    <Td>
                      <div style={{ fontWeight: 500 }}>{r.item_title || `#${r.item_id}`}</div>
                      <SecondaryText>{r.course_code ? `${r.course_code} · ` : ''}{r.course_name}</SecondaryText>
                    </Td>
                    <Td><TypeChip>{r.item_type}</TypeChip></Td>
                    <Td>
                      <Justification>{justText}</Justification>
                      {long && (
                        <ExpandBtn onClick={() => setExpanded((m) => ({ ...m, [r.id]: !isExpanded }))}>
                          {isExpanded ? <>Hide <FiChevronUp size={11} /></> : <>Read more <FiChevronDown size={11} /></>}
                        </ExpandBtn>
                      )}
                    </Td>
                    <Td>
                      <span style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--text-muted)', fontSize: 12.5 }}>
                        {formatDateTime(r.requested_at)}
                      </span>
                    </Td>
                    <Td>
                      {r.status !== 'pending' && <Pill className={r.status}>{r.status}</Pill>}
                      {r.status === 'pending' && (
                        <span style={{
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          color: '#ea580c', // orange
                        }}>
                          Awaiting review
                        </span>
                      )}
                      {r.status === 'approved' && r.access_expires_at && (
                        <SecondaryText>until {formatDateTime(r.access_expires_at)}</SecondaryText>
                      )}
                      {r.status !== 'pending' && r.reviewer_first_name && (
                        <SecondaryText>
                          by {r.reviewer_first_name} {r.reviewer_last_name || ''}
                        </SecondaryText>
                      )}
                    </Td>
                    <Td style={{ textAlign: 'right' }}>
                      {r.status === 'pending' ? (
                        <Actions>
                          <ApproveBtn onClick={() => setApproveTarget(r)}>
                            <FiCheck size={13} /> Approve
                          </ApproveBtn>
                          <DenyBtn onClick={() => setDenyTarget(r)}>
                            <FiX size={13} /> Deny
                          </DenyBtn>
                        </Actions>
                      ) : (
                        <InlineMeta>—</InlineMeta>
                      )}
                    </Td>
                  </Row>
                );
              })}
            </tbody>
          </Table>
        </TableScroll>
      </TableCard>
      </section>}
        </MainStack>
      </ContentShell>

      <ApproveModal
        open={!!approveTarget}
        onClose={() => setApproveTarget(null)}
        onConfirm={handleApprove}
        request={approveTarget}
      />
      <DenyModal
        open={!!denyTarget}
        onClose={() => setDenyTarget(null)}
        onConfirm={handleDeny}
        request={denyTarget}
      />
    </Page>
  );
}
